/**
 * Фильтр списка звонков по критериям (#865; спека
 * `docs/plans/2026-09-18-custom-criteria.md` §6.1, решения —
 * `docs/plans/2026-09-29-criteria-list-filter.md`).
 *
 * Отдельным файлом, а не в `call-criteria.ts`: тот импортирует из `records.ts`,
 * а схема запроса списка — из этого файла, и общий файл замкнул бы цикл.
 *
 * Условие едет в query строкой, повторяющимся параметром `criterion`, как
 * `projectIds`: ссылка из отчёта по критерию (#866) читается глазами, а JSON в
 * query прецедента не имеет.
 *
 *   `<id>:flag:1` · `<id>:flag:0` · `<id>:score:<от>:<до>` · `<id>:choice:<код>,<код>`
 */

import { z } from 'zod/v4';

/** До трёх условий одновременно, соединение «И» (спека §6.1). */
export const RECORD_CRITERIA_FILTER_MAX = 3;

/**
 * id критерия. Сейчас это UUID (`crypto.randomUUID()` в маршруте создания), но
 * встроенные критерии (#868) ещё не заведены — поэтому проверяется только
 * алфавит: без `:` и `,`, на которых держится разбор строки.
 */
export const criterionIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{1,64}$/, 'Invalid criterion id');

/**
 * Алфавит кодов значений. Источник — `CALL_CRITERION_CODE_PATTERN` в
 * `@aica/core`; здесь он записан на месте, а совпадение держит тест
 * (`test/criteria-filter.test.ts`). Импорт не годится: проверки на обычном Node
 * (`check:public-error-codes`) грузят эту схему, а в рабочей копии `@aica/core`
 * резолвится в основной checkout — новая константа там появится только после
 * мержа, и пуш из рабочей копии падал бы.
 */
export const OPTION_CODE_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

const optionCodeSchema = z.string().regex(OPTION_CODE_PATTERN, 'Invalid option code');

const scoreBound = z.number().int().min(0).max(100);

/**
 * Сколько значений берёт одно условие-список. Не `CALL_CRITERION_LIMITS.options`:
 * тот ограничивает только ДЕЙСТВУЮЩИЕ варианты, а архивные копятся без
 * предела и тоже предлагаются фильтром — «выбрать все» у критерия с историей
 * правок упёрлось бы в 400. Значения вписываются в запрос литералами и
 * параметров D1 не тратят; потолок держит лишь длину строки запроса.
 */
export const RECORD_CRITERION_CHOICE_CODES_MAX = 100;

export const recordCriterionConditionSchema = z.discriminatedUnion('kind', [
  z.object({
    criterionId: criterionIdSchema,
    kind: z.literal('flag'),
    /** `true` — сработал, `false` — не сработал; воздержание не попадает ни туда, ни туда. */
    triggered: z.boolean(),
  }),
  z
    .object({
      criterionId: criterionIdSchema,
      kind: z.literal('score'),
      /** Включительно с обеих сторон. */
      min: scoreBound,
      max: scoreBound,
    })
    .refine((c) => c.min <= c.max, { message: 'Score range is inverted' }),
  z.object({
    criterionId: criterionIdSchema,
    kind: z.literal('choice'),
    /** Любое из значений: действующие, архивные и `not_mentioned`. */
    codes: z.array(optionCodeSchema).min(1).max(RECORD_CRITERION_CHOICE_CODES_MAX),
  }),
]);

export type RecordCriterionCondition = z.infer<typeof recordCriterionConditionSchema>;

/**
 * Строка условия → объект для схемы. Не валидирует сам: неразборчивая строка
 * отдаёт то, что схема отвергнет с понятным сообщением, а не бросает.
 */
function parseConditionString(raw: string): unknown {
  const [criterionId, kind, ...rest] = raw.split(':');
  switch (kind) {
    case 'flag':
      return rest.length === 1 && (rest[0] === '1' || rest[0] === '0')
        ? { criterionId, kind, triggered: rest[0] === '1' }
        : { criterionId, kind };
    case 'score': {
      const [min, max] = rest.map((part) =>
        /^\d{1,3}$/.test(part) ? Number(part) : NaN
      );
      return rest.length === 2 ? { criterionId, kind, min, max } : { criterionId, kind };
    }
    case 'choice': {
      const [codes] = rest;
      return rest.length === 1 && codes !== undefined
        ? { criterionId, kind, codes: codes.split(',') }
        : { criterionId, kind };
    }
    default:
      return { criterionId, kind };
  }
}

/** Условие → строка параметра `criterion`; обратное `parseConditionString`. */
export function formatRecordCriterionCondition(
  condition: RecordCriterionCondition
): string {
  switch (condition.kind) {
    case 'flag':
      return `${condition.criterionId}:flag:${condition.triggered ? '1' : '0'}`;
    case 'score':
      return `${condition.criterionId}:score:${condition.min}:${condition.max}`;
    case 'choice':
      return `${condition.criterionId}:choice:${condition.codes.join(',')}`;
  }
}

// Длина строки одного условия: id (64) + `:choice:` + коды до 40 символов с
// запятой — предел должен вмещать потолок значений, а не спорить с ним.
const CONDITION_STRING_MAX = 64 + 8 + RECORD_CRITERION_CHOICE_CODES_MAX * 41;

const conditionFromStringSchema = z
  .string()
  .max(CONDITION_STRING_MAX)
  .transform(parseConditionString)
  .pipe(recordCriterionConditionSchema);

/**
 * Параметр `criterion` списка звонков: одна строка или повторы. Пусто —
 * `undefined`, не `[]`: ниже по стеку «нет фильтра» одно значение.
 */
export const recordCriteriaFilterSchema = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform((raw) => (raw === undefined ? [] : Array.isArray(raw) ? raw : [raw]))
  .pipe(
    z
      .array(conditionFromStringSchema)
      .max(RECORD_CRITERIA_FILTER_MAX, `At most ${RECORD_CRITERIA_FILTER_MAX} criteria`)
      .refine(
        (conditions) =>
          new Set(conditions.map((c) => c.criterionId)).size === conditions.length,
        { message: 'One condition per criterion' }
      )
  )
  .transform((conditions) => (conditions.length > 0 ? conditions : undefined));

/**
 * Критерий в перечне `criteria-filter-options`: чем фильтровать и что показать. Варианты
 * списка — с архивными: ответы на них лежат в зеркале и находятся фильтром.
 */
export const criterionFilterOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  resultType: z.enum(['flag', 'score', 'choice']),
  archived: z.boolean(),
  options: z
    .array(z.object({ code: z.string(), label: z.string(), archived: z.boolean() }))
    .nullable(),
});

export type CriterionFilterOption = z.infer<typeof criterionFilterOptionSchema>;

/**
 * `GET /api/v1/records/criteria-filter-options`: живые критерии и архивные с
 * ответами в зеркале; пусто — у организации критериев нет или они выключены.
 *
 * Отдельным маршрутом, не полем `filter-options` (#1293): сбой чтения — ошибка
 * ответа, а не пустой перечень внутри успешного, который интерфейс принял бы
 * за «критериев нет» и снял бы выбранные условия молча.
 */
export const recordsCriteriaFilterOptionsResponseSchema = z.object({
  criteria: z.array(criterionFilterOptionSchema),
});
